
import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { generateUUID } from '@/lib/utils';

export type UploadResult = string;

export function useStorage() {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Upload a file to storage
   * @param path Path to store the file at
   * @param file File to upload
   * @returns URL of the uploaded file
   */
  const uploadFile = async (path: string, file: File): Promise<string> => {
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // For development/demo, just return a fake URL
      if (import.meta.env.DEV || !file) {
        await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate network latency
        
        // Update progress for UI feedback
        const interval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 95) {
              clearInterval(interval);
              return 95;
            }
            return prev + 10;
          });
        }, 150);
        
        // Wait a bit then set 100%
        setTimeout(() => {
          clearInterval(interval);
          setUploadProgress(100);
          setIsUploading(false);
        }, 1500);
        
        // Return a temporary object URL or dummy URL for development
        if (file) {
          return URL.createObjectURL(file);
        }
        return `https://example.com/storage/demo-${generateUUID()}.png`;
      }

      // Use the existing company_logos bucket
      const bucket = 'company_logos';
      const filePath = `${path.replace(/^\/+/, '')}-${Date.now()}`;

      console.log(`Uploading to bucket: ${bucket}, path: ${filePath}`);

      // Upload the file - Using compatible Supabase storage options
      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      setUploadProgress(100);
      setIsUploading(false);
      
      console.log('Upload successful, public URL:', urlData.publicUrl);
      return urlData.publicUrl;
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsUploading(false);
      console.error('Error uploading file:', err);
      throw err;
    }
  };

  /**
   * Delete a file from storage
   * @param path Path to the file
   */
  const deleteFile = async (path: string): Promise<void> => {
    try {
      // For development/demo, just simulate success
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network latency
        return;
      }

      // Extract the filename from the URL
      const pathParts = path.split('/');
      const fileName = pathParts[pathParts.length - 1];
      
      console.log(`Deleting file: ${fileName} from storage`);

      // Use the existing company_logos bucket
      const bucket = 'company_logos';
      const { error: deleteError } = await supabase.storage
        .from(bucket)
        .remove([fileName]);

      if (deleteError) {
        console.error('Delete error:', deleteError);
        throw deleteError;
      }
      
      console.log('File deleted successfully');
    } catch (err: unknown) {
      console.error('Error deleting file:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  };

  return {
    uploadFile,
    deleteFile,
    isUploading,
    uploadProgress,
    error,
  };
}
